import { useState, useEffect, useCallback } from 'react'
import { audioManager } from '@/audio'
import { spotifyPlayer } from '@/audio/SpotifyPlayer'
import type { SpotifyTrack } from '@/api/spotify/types'
import { useUIStore } from '@/stores/uiStore'
import { usePlaybackStore, type PlaybackMode } from '@/stores/playbackStore'
import type { NowPlayingTrack } from '@/stores/playbackStore'

interface AudioPreviewPlayerProps {
  tracks: SpotifyTrack[]
  artistName: string
}

/**
 * Unified audio player component supporting both SDK (full tracks) and preview (30s) modes
 */
export function AudioPreviewPlayer({
  tracks,
  artistName,
}: AudioPreviewPlayerProps): React.JSX.Element | null {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0)

  // UI Store (for preview mode compatibility)
  const previewingTrackId = useUIStore((state) => state.previewingTrackId)
  const setPreviewingTrack = useUIStore((state) => state.setPreviewingTrack)
  const previewVolume = useUIStore((state) => state.previewVolume)
  const setPreviewVolume = useUIStore((state) => state.setPreviewVolume)

  // Playback Store (for SDK mode)
  const mode = usePlaybackStore((state) => state.mode)
  const playerState = usePlaybackStore((state) => state.playerState)
  const sdkReady = usePlaybackStore((state) => state.sdkReady)
  const setCurrentTrack = usePlaybackStore((state) => state.setCurrentTrack)
  const setPlaybackIsPlaying = usePlaybackStore((state) => state.setIsPlaying)

  // Determine effective mode
  const effectiveMode: PlaybackMode = sdkReady && mode === 'sdk' ? 'sdk' : 'preview'

  // For preview mode, filter tracks with preview URLs
  const playableTracks =
    effectiveMode === 'sdk'
      ? tracks // SDK can play all tracks
      : tracks.filter((track) => track.preview_url !== null)

  const currentTrack = playableTracks[currentTrackIndex]

  // Sync with SDK player state
  useEffect(() => {
    if (effectiveMode !== 'sdk' || !playerState) return

    setIsPlaying(playerState.isPlaying)
    setCurrentTime(playerState.position / 1000) // Convert ms to seconds
    setDuration(playerState.duration / 1000)

    // Find matching track index when SDK plays a different track
    if (playerState.currentTrack?.id) {
      const index = playableTracks.findIndex(
        (t) => t.id === playerState.currentTrack?.id
      )
      if (index !== -1 && index !== currentTrackIndex) {
        setCurrentTrackIndex(index)
      }
    }
  }, [effectiveMode, playerState, playableTracks, currentTrackIndex])

  // Sync with audio manager state (preview mode)
  useEffect(() => {
    if (effectiveMode !== 'preview') return

    const unsubPlay = audioManager.on('play', () => {
      setIsPlaying(true)
    })
    const unsubPause = audioManager.on('pause', () => {
      setIsPlaying(false)
    })
    const unsubEnded = audioManager.on('ended', () => {
      setIsPlaying(false)
      setCurrentTime(0)
      // Auto-play next track
      if (currentTrackIndex < playableTracks.length - 1) {
        setCurrentTrackIndex((prev) => prev + 1)
      }
    })
    const unsubTimeUpdate = audioManager.on('timeupdate', (data: unknown) => {
      const timeData = data as { currentTime: number; duration: number }
      setCurrentTime(timeData.currentTime)
      setDuration(timeData.duration)
    })

    return () => {
      unsubPlay()
      unsubPause()
      unsubEnded()
      unsubTimeUpdate()
    }
  }, [effectiveMode, currentTrackIndex, playableTracks.length])

  // Update volume when store changes
  useEffect(() => {
    if (effectiveMode === 'preview') {
      audioManager.setVolume(previewVolume)
    } else if (effectiveMode === 'sdk') {
      spotifyPlayer.setVolume(previewVolume)
    }
  }, [previewVolume, effectiveMode])

  // Check if current track is playing (preview mode)
  useEffect(() => {
    if (effectiveMode !== 'preview') return

    if (currentTrack && audioManager.getCurrentTrackId() === currentTrack.id) {
      setIsPlaying(audioManager.isPlaying())
    } else {
      setIsPlaying(false)
    }
  }, [currentTrack, effectiveMode])

  const handlePlayPause = useCallback(async () => {
    if (!currentTrack) return

    // Helper to set current track in global store
    const updateGlobalTrack = () => {
      const nowPlayingTrack: NowPlayingTrack = {
        id: currentTrack.id,
        name: currentTrack.name,
        artistName: artistName,
        albumName: currentTrack.album.name,
        albumImageUrl: currentTrack.album.images[0]?.url ?? null,
        durationMs: currentTrack.duration_ms,
        uri: currentTrack.uri,
        previewUrl: currentTrack.preview_url,
      }
      setCurrentTrack(nowPlayingTrack)
      setPlaybackIsPlaying(true)
    }

    if (effectiveMode === 'sdk') {
      // SDK mode - full track playback
      const currentlyPlayingThisTrack = spotifyPlayer.getCurrentTrackId() === currentTrack.id
      const sdkIsPlaying = spotifyPlayer.isPlaying()

      if (sdkIsPlaying && currentlyPlayingThisTrack) {
        try {
          await spotifyPlayer.pause()
          setPreviewingTrack(null)
          setPlaybackIsPlaying(false)
          setIsPlaying(false)
        } catch (err) {
          console.error('[AudioPreviewPlayer] Failed to pause:', err)
        }
      } else {
        try {
          // Activate element for browser autoplay policy
          await spotifyPlayer.activateElement()

          // Play all tracks starting from current index
          const trackUris = playableTracks.map((t) => t.uri)
          await spotifyPlayer.playTracks(trackUris, currentTrackIndex)

          // Update state optimistically
          setPreviewingTrack(currentTrack.id)
          updateGlobalTrack()
          setIsPlaying(true)
        } catch (err) {
          console.error('[AudioPreviewPlayer] Failed to play tracks:', err)
          // Try falling back to preview mode if SDK fails
          if (currentTrack.preview_url) {
            try {
              await audioManager.play(currentTrack.preview_url, currentTrack.id)
              setPreviewingTrack(currentTrack.id)
              updateGlobalTrack()
              setIsPlaying(true)
            } catch (previewErr) {
              console.error('[AudioPreviewPlayer] Preview fallback also failed:', previewErr)
            }
          }
        }
      }
    } else {
      // Preview mode - 30s previews
      if (!currentTrack.preview_url) return

      if (isPlaying && audioManager.getCurrentTrackId() === currentTrack.id) {
        await audioManager.pause()
        setPreviewingTrack(null)
        setPlaybackIsPlaying(false)
      } else {
        await audioManager.play(currentTrack.preview_url, currentTrack.id)
        setPreviewingTrack(currentTrack.id)
        updateGlobalTrack()
      }
    }
  }, [
    currentTrack,
    effectiveMode,
    isPlaying,
    playableTracks,
    currentTrackIndex,
    setPreviewingTrack,
    artistName,
    setCurrentTrack,
    setPlaybackIsPlaying,
  ])

  const handlePrevious = useCallback(async () => {
    if (currentTrackIndex > 0) {
      const newIndex = currentTrackIndex - 1
      setCurrentTrackIndex(newIndex)
      setCurrentTime(0)

      // Auto-play in SDK mode
      if (effectiveMode === 'sdk' && isPlaying) {
        const trackUris = playableTracks.map((t) => t.uri)
        await spotifyPlayer.playTracks(trackUris, newIndex)
      }
    }
  }, [currentTrackIndex, effectiveMode, isPlaying, playableTracks])

  const handleNext = useCallback(async () => {
    if (currentTrackIndex < playableTracks.length - 1) {
      const newIndex = currentTrackIndex + 1
      setCurrentTrackIndex(newIndex)
      setCurrentTime(0)

      // Auto-play in SDK mode
      if (effectiveMode === 'sdk' && isPlaying) {
        const trackUris = playableTracks.map((t) => t.uri)
        await spotifyPlayer.playTracks(trackUris, newIndex)
      }
    }
  }, [currentTrackIndex, playableTracks, effectiveMode, isPlaying])

  const handleSeek = useCallback(
    async (e: React.MouseEvent<HTMLDivElement>) => {
      if (!duration) return
      const rect = e.currentTarget.getBoundingClientRect()
      const x = e.clientX - rect.left
      const percentage = x / rect.width
      const newTime = percentage * duration

      if (effectiveMode === 'sdk') {
        await spotifyPlayer.seek(newTime * 1000) // Convert to ms
      } else {
        audioManager.seek(newTime)
      }
    },
    [duration, effectiveMode]
  )

  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newVolume = parseFloat(e.target.value)
      setPreviewVolume(newVolume)
    },
    [setPreviewVolume]
  )

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // No playable tracks
  if (playableTracks.length === 0) {
    return (
      <div className="px-4 py-4 border-t border-white/10">
        <div className="flex items-center gap-3 text-gray-400">
          <div className="w-10 h-10 rounded bg-white/5 flex items-center justify-center">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-300">
              Previews unavailable
            </p>
            <p className="text-xs text-gray-500">Listen on Spotify instead</p>
          </div>
        </div>
      </div>
    )
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0
  // Use both local state and SDK state for more reliable detection
  const isCurrentTrackPlaying =
    currentTrack && (
      (previewingTrackId === currentTrack.id && isPlaying) ||
      (effectiveMode === 'sdk' && spotifyPlayer.getCurrentTrackId() === currentTrack.id && spotifyPlayer.isPlaying())
    )

  return (
    <div className="border-t border-white/10 bg-gradient-to-b from-black/0 to-black/30">
      {/* Track info */}
      <div className="px-4 pt-3 pb-2">
        <div className="flex items-center gap-3">
          {/* Album art */}
          {currentTrack?.album.images[0] && (
            <img
              src={currentTrack.album.images[0].url}
              alt={currentTrack.album.name}
              className="w-10 h-10 rounded object-cover"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-white text-sm font-medium truncate">
                {currentTrack?.name}
              </p>
              {/* Mode badge */}
              {effectiveMode === 'sdk' && (
                <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-medium bg-[#1DB954]/20 text-[#1DB954] rounded">
                  FULL
                </span>
              )}
            </div>
            <p className="text-gray-400 text-xs truncate">{artistName}</p>
          </div>
          {/* Track counter */}
          <span className="text-gray-500 text-xs">
            {currentTrackIndex + 1}/{playableTracks.length}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-4 pb-2">
        <div
          className="h-1 bg-white/10 rounded-full cursor-pointer group"
          onClick={handleSeek}
        >
          <div
            className="h-full bg-[#1DB954] rounded-full relative transition-all group-hover:bg-[#1ed760]"
            style={{ width: `${progress}%` }}
          >
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>{formatTime(currentTime)}</span>
          <span>
            {formatTime(
              duration || (effectiveMode === 'preview' ? 30 : currentTrack?.duration_ms ? currentTrack.duration_ms / 1000 : 0)
            )}
          </span>
        </div>
      </div>

      {/* Controls */}
      <div className="px-4 pb-3 flex items-center justify-between">
        {/* Playback controls */}
        <div className="flex items-center gap-2">
          {/* Previous */}
          <button
            onClick={handlePrevious}
            disabled={currentTrackIndex === 0}
            className="p-2 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Previous track"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
            </svg>
          </button>

          {/* Play/Pause */}
          <button
            onClick={handlePlayPause}
            className="p-3 bg-white hover:scale-105 rounded-full transition-transform"
            aria-label={isCurrentTrackPlaying ? 'Pause' : 'Play'}
          >
            {isCurrentTrackPlaying ? (
              <svg
                className="w-5 h-5 text-black"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 text-black ml-0.5"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          {/* Next */}
          <button
            onClick={handleNext}
            disabled={currentTrackIndex >= playableTracks.length - 1}
            className="p-2 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Next track"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
            </svg>
          </button>
        </div>

        {/* Volume control */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPreviewVolume(previewVolume > 0 ? 0 : 0.5)}
            className="p-1 text-gray-400 hover:text-white transition-colors"
            aria-label={previewVolume > 0 ? 'Mute' : 'Unmute'}
          >
            {previewVolume === 0 ? (
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
              </svg>
            ) : previewVolume < 0.5 ? (
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z" />
              </svg>
            ) : (
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
              </svg>
            )}
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={previewVolume}
            onChange={handleVolumeChange}
            className="w-16 h-1 bg-white/10 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white"
            aria-label="Volume"
          />
        </div>
      </div>
    </div>
  )
}
